import { ClientOngoingLoad, OngoingLoadStatus, timeZonePreference } from '@numeo/types-core'
import { getBoardRange } from 'pages/dashboard/pages/updater-agent/pages/scheduler/utils/timezoneUtils'
import { AppDispatch, RootState } from 'store'
import { removeAssignedLoadById, removeLoadFromSchedulerBucketsById, setUnassignedLoads, upsertAssignedLoad } from 'store/schedulerSlice'

export function syncSchedulerForLoad(load: ClientOngoingLoad, dispatch: AppDispatch, getState: () => RootState) {
    const state = getState()
    const selectedDate = state.scheduler?.selectedDate
    // The very range the grid is drawing — custom gross range included, same anchor, same cadence seam,
    // same timezone. This used to build its own Monday week off the browser clock, which disagreed with the
    // board for any dispatcher whose OS timezone differs from their scheduler preference, and ignored a
    // custom range outright: a live update could then add or drop a load the grid plainly shows on the
    // other side of the boundary.
    const boardRange = selectedDate
        ? getBoardRange(selectedDate, state.scheduler?.userTimeZone ?? timeZonePreference.UTC, state.auth?.application, state.scheduler?.grossDateRange)
        : null
    // An unparseable date yields invalid bounds, and every comparison against an Invalid Date is false — so
    // the overlap test below would answer "outside the range" for every load and quietly strip the board.
    // Bail out instead and leave what is on screen alone, as this did before it read the board's own range.
    const week = boardRange?.start.isValid && boardRange.end.isValid ? { start: boardRange.start.toJSDate(), end: boardRange.end.toJSDate() } : null
    if (!week) return
    const overlaps = loadOverlapsRange(load, week)

    // Assigned
    if (load?.truckId) {
        if (overlaps) {
            // Upsert into assigned list
            dispatch(upsertAssignedLoad(load))
        } else {
            // If outside current week, remove if present
            const id = (load as ClientOngoingLoad).id ?? (load as ClientOngoingLoad).sourceRateConfId
            if (id) dispatch(removeAssignedLoadById(String(id)))
        }
        // Also ensure it's not present in unassigned
        const unassigned = state.scheduler.unassigned
        const nextUnassigned = unassigned.filter((u) => u.sourceRateConfId !== load.sourceRateConfId)
        if (nextUnassigned.length !== unassigned.length) dispatch(setUnassignedLoads(nextUnassigned))
        return
    }

    // Unassigned UPCOMING loads (no truck)
    if (!load?.truckId && load?.status === OngoingLoadStatus.UPCOMING) {
        // Always remove from assigned list when it becomes unassigned
        const id = (load as ClientOngoingLoad).id ?? (load as ClientOngoingLoad).sourceRateConfId
        if (id) dispatch(removeLoadFromSchedulerBucketsById(String(id)))

        const unassigned = state.scheduler.unassigned
        const exists = unassigned.some((u) => u.sourceRateConfId === load.sourceRateConfId)

        if (overlaps) {
            // Update existing item or add new one
            const next = exists ? unassigned.map((u) => (u.sourceRateConfId === load.sourceRateConfId ? load : u)) : [...unassigned, load]
            dispatch(setUnassignedLoads(next))
        } else if (exists) {
            // Remove from unassigned if it doesn't overlap with current week
            dispatch(setUnassignedLoads(unassigned.filter((u) => u.sourceRateConfId !== load.sourceRateConfId)))
        }
    }
}

export function removeFromScheduler(load: ClientOngoingLoad, dispatch: AppDispatch, getState: () => RootState) {
    const state = getState()
    const id = (load as ClientOngoingLoad).id ?? (load as ClientOngoingLoad).sourceRateConfId
    if (id) dispatch(removeAssignedLoadById(String(id)))
    const unassigned = state.scheduler.unassigned
    const nextUnassigned = unassigned.filter((u) => u.sourceRateConfId !== load.sourceRateConfId)
    if (nextUnassigned.length !== unassigned.length) dispatch(setUnassignedLoads(nextUnassigned))
}

// -----------------------------
// Scheduler sync helpers
// -----------------------------

function loadOverlapsRange(load: ClientOngoingLoad, range: { start: Date; end: Date }): boolean {
    const route = load?.route || []
    if (!Array.isArray(route) || route.length === 0) return false
    const firstArrival = route[0]?.arrivalDate ? new Date(route[0].arrivalDate) : null
    const lastDeadline = route[route.length - 1]?.deadlineDate ? new Date(route[route.length - 1].deadlineDate!) : null
    if (!firstArrival || !lastDeadline || isNaN(firstArrival.getTime()) || isNaN(lastDeadline.getTime())) return false
    const X = firstArrival
    const Y = lastDeadline
    const A = range.start
    const B = range.end
    // Overlap if (X<=A<=Y) or (X<=B<=Y) or (A<=X and B>=Y)
    return (X <= A && Y >= A) || (X <= B && Y >= B) || (A <= X && B >= Y)
}
